import {
  Suspense,
  createContext,
  lazy,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";

import CircularLoader from "shared/components/atoms/CircularLoader";
import { ComponentErrorBoundary } from "shared/components/atoms/ComponentErrorBoundary";
import componentLoader from "shared/utils/componentLoader";
import { useAuthContext } from "shared/utils/hooks/useAuthContext";
import { useGlobalContext } from "shared/utils/hooks/useGlobalContext";
import openNotificationWithIcon from "shared/utils/openNotificationWithIcon";

import { captureException, captureMessage } from "@sentry/nextjs";

import { AuthModalLoginTemplateFallback } from "../components/templates/Login/styles";
import { IAuthModalContext, TAuthModalPages } from "../types";

const Register = lazy(() =>
  componentLoader(
    () =>
      import(
        /* webpackChunkFilename: 'Register', filename: 'Register' */
        "../components/templates/Register"
      ),
  ),
);

const RegistrationClosed = lazy(() =>
  componentLoader(
    () =>
      import(
        /* webpackChunkFilename: 'RegistrationClosed', filename: 'RegistrationClosed' */
        "../components/templates/RegistrationClosed"
      ),
  ),
);

const ForgotPassword = lazy(() =>
  componentLoader(
    () =>
      import(
        /* webpackChunkFilename: 'ForgotPassword', filename: 'ForgotPassword' */
        "../components/templates/ForgotPassword"
      ),
  ),
);

const InitialScreen = lazy(() =>
  componentLoader(
    () =>
      import(
        /* webpackChunkFilename: 'InitialScreen', filename: 'InitialScreen' */
        "../components/templates/Initial"
      ),
  ),
);

const Login = lazy(() =>
  componentLoader(
    () =>
      import(
        /* webpackChunkFilename: 'Login', filename: 'Login' */
        "../components/templates/Login"
      ),
  ),
);

const FIRST_PAGE = "first";
const CONFIRMATION_PAGE = "confirmation";

export const AuthModalContext = createContext({} as IAuthModalContext);

export const AuthModalContextProvider = ({ children }) => {
  const { state, setState } = useGlobalContext();
  const { loggedUser } = useAuthContext();

  const choosePage: TAuthModalPages = state?.modal?.page;
  const onLoginCallback = state?.modal?.onLoginCallback;
  const shouldCallbackFunction = state?.modal?.shouldCallbackFunction;

  const userSignUpAnyoneCanCreateAccount =
    state?.tenancy?.settings?.users?.userSignUpAnyoneCanCreateAccount;
  const userSignInWithGoogle =
    state?.tenancy?.settings?.users?.userSignInWithGoogle;

  const isUserSignUpAnyoneCanCreateAccountEnabled =
    userSignUpAnyoneCanCreateAccount === "1";
  const isUserSignInWithGoogleEnabled = userSignInWithGoogle === "1";

  const hasToShowRegistrationClosedModal =
    isUserSignUpAnyoneCanCreateAccountEnabled || isUserSignInWithGoogleEnabled;

  const [loginPage, setLoginPage] = useState<TAuthModalPages>(choosePage);
  const [prevHistory, setPrevHistory] = useState([]);

  const shouldShowArrowNavigation = prevHistory?.length > 0;

  const handleCloseAuthModal = useCallback(() => {
    setPrevHistory([]);
    setLoginPage(choosePage);
    setState((prev) => ({
      ...prev,
      modal: {
        ...prev.modal,
        onLoginCallback: null,
        shouldCallbackFunction: false,
        open: false,
      },
    }));
  }, [setState, choosePage]);

  useEffect(() => {
    if ((!onLoginCallback && !loggedUser) || !shouldCallbackFunction) return;

    try {
      if (onLoginCallback) onLoginCallback();
      handleCloseAuthModal();
    } catch (error) {
      console.log("Error (onLoginCallback): ", error);
      captureException(error);
      captureMessage("Error (onLoginCallback)");
    }
  }, [
    onLoginCallback,
    loggedUser,
    shouldCallbackFunction,
    handleCloseAuthModal,
  ]);

  const handleTemporaryHistory = useCallback(() => {
    setPrevHistory((prevHistory) => {
      const tempHistory = [...prevHistory];
      const lastItem = tempHistory.pop();
      setLoginPage(lastItem || FIRST_PAGE);
      return tempHistory;
    });
  }, []);

  const handleBackAuthModal = useCallback(
    () => loginPage !== FIRST_PAGE && handleTemporaryHistory(),
    [handleTemporaryHistory, loginPage],
  );

  const handleNavigate = useCallback(
    (page) => {
      setPrevHistory([...prevHistory, loginPage]);

      const closeAuthModal = page === CONFIRMATION_PAGE;
      if (closeAuthModal) {
        handleCloseAuthModal();
        openNotificationWithIcon("success", "Conta criada com sucesso!");
      }

      setLoginPage(page);
    },
    [handleCloseAuthModal, prevHistory, loginPage],
  );

  useLayoutEffect(() => {
    setLoginPage(() => choosePage || FIRST_PAGE);
  }, [choosePage]);

  const RenderPage = useCallback(() => {
    switch (loginPage) {
      case "first":
        return (
          <ComponentErrorBoundary>
            <Suspense fallback={<Fallback />}>
              <InitialScreen />
            </Suspense>
          </ComponentErrorBoundary>
        );
      case "login":
        return (
          <ComponentErrorBoundary>
            <Suspense fallback={<Fallback />}>
              <Login />
            </Suspense>
          </ComponentErrorBoundary>
        );
      case "register":
        return hasToShowRegistrationClosedModal ? (
          <ComponentErrorBoundary>
            <Suspense fallback={<Fallback />}>
              <Register />
            </Suspense>
          </ComponentErrorBoundary>
        ) : (
          <ComponentErrorBoundary>
            <Suspense fallback={<Fallback />}>
              <RegistrationClosed />
            </Suspense>
          </ComponentErrorBoundary>
        );

      case "forgot":
        return (
          <ComponentErrorBoundary>
            <Suspense fallback={<Fallback />}>
              <ForgotPassword navigate={handleNavigate} />
            </Suspense>
          </ComponentErrorBoundary>
        );
      default:
        return null;
    }
  }, [handleNavigate, loginPage, userSignUpAnyoneCanCreateAccount]);

  return (
    <AuthModalContext.Provider
      value={{
        loginPage,
        userSignUpAnyoneCanCreateAccount,
        shouldShowArrowNavigation,
        isUserSignUpAnyoneCanCreateAccountEnabled,
        handleBackAuthModal,
        handleNavigate,
        handleCloseAuthModal,
        RenderPage,
      }}
    >
      {children}
    </AuthModalContext.Provider>
  );
};

const Fallback = () => (
  <AuthModalLoginTemplateFallback>
    <CircularLoader />
  </AuthModalLoginTemplateFallback>
);
